import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DwButtonCommand,
  DwComponent,
  DwComponentType,
  DwContextService,
  DwEventService,
  DwExpressionStateService,
  DwModalButtonStandardConfigs,
  DwModalConfig,
  DwModalService,
  DwModalSize,
  DwQueryFilterGroup,
  DwSecurityUserService
} from '@devwareapps/devware-cap';
import { forkJoin, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, skip, take, takeUntil } from 'rxjs/operators';
import { AdminManagerService } from '../../../../shared/services/admin-manager.service';
import { ReportDashboardDefinition } from '../../models/report-dashboard-definition.model';
import { ReportMetricTileResult } from '../../models/report-dashboard-runtime.model';
import { ReportGroupFilterSession } from '../../models/report-filter-preset.model';
import {
  ReportDashboardConfigRecord,
  ReportDashboardGroupConfigRecord,
  ReportDashboardWidgetConfigRecord
} from '../../models/report-dashboard-config-record.model';
import {
  ReportDashboardGroupDefinition,
  ReportDashboardGroupTabDefinition,
  ReportDashboardRouteConfig
} from '../../models/report-dashboard-group-definition.model';
import { ReportDashboardConfigService } from '../../services/report-dashboard-config.service';
import { ConfiguredReportDashboardConfigComponent } from './configured-report-dashboard-config.component';
import { ReportDashboardComponent } from '../report-dashboard/report-dashboard.component';
import { ChartTileDisplayPreferenceService } from '../../../../shared/services/chart-tile-display-preference.service';
import {
  ReportDashboardGroupModalComponent,
  ReportDashboardGroupModalResult,
  ReportDashboardPendingClone
} from '../report-dashboard-group-modal/report-dashboard-group-modal.component';
import {
  ReportDashboardCreateModalComponent,
  ReportDashboardCreateModalResult
} from '../report-dashboard-create-modal/report-dashboard-create-modal.component';

interface ResolvedDashboardTab {
  tab: ReportDashboardGroupTabDefinition;
  definition: ReportDashboardDefinition;
}

interface DashboardNavigationItem {
  dashboardKey: string;
  display: string;
}

const ADD_NEW_DASHBOARD_KEY = '__add-new-dashboard__';

@DwComponent({
  key: 'configured-report-dashboard',
  name: 'Reporting Dashboard',
  componentType: DwComponentType.pageComponent,
  isGlobal: true,
  configComponent: ConfiguredReportDashboardConfigComponent
})
@Component({
  selector: 'app-configured-report-dashboard-page',
  templateUrl: './configured-report-dashboard-page.component.html',
  styleUrls: ['./configured-report-dashboard-page.component.scss']
})
export class ConfiguredReportDashboardPageComponent implements OnInit, OnDestroy {
  @ViewChild('dashboardComponent') dashboardComponent: ReportDashboardComponent;

  definition: ReportDashboardDefinition;
  incomingTile: { definition: ReportDashboardDefinition; selection: ReportMetricTileResult };
  groupDefinition: ReportDashboardGroupDefinition;
  groupFilterSession: ReportGroupFilterSession = {};
  resolvedTabs: ResolvedDashboardTab[] = [];
  dashboardNavigationItems: DashboardNavigationItem[] = [];
  activeTab: ResolvedDashboardTab;
  activeDashboardKey: string;
  expressionContext: any;
  loading = false;
  errorMessage: string;
  showChartGridFirstByDefault = false;
  canEditDashboard = false;
  designMode = false;
  designerLoading = false;
  designerSaving = false;
  designerGroupSaving = false;
  designerDirty = false;
  designerErrorMessage: string;
  designerEditorMode: 'design' | 'json' = 'design';
  workingDefinition: ReportDashboardDefinition;
  workingConfigText = '';
  reusableWidgetRecords: ReportDashboardWidgetConfigRecord[] = [];
  designerPreviewFilters = new DwQueryFilterGroup();

  private activeDashboardRecord: ReportDashboardConfigRecord;
  private contextRefreshSubscription: Subscription;
  private contextRefreshKey: string;
  private groupLoadGeneration = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private configService: ReportDashboardConfigService,
    private expressionState: DwExpressionStateService,
    private route: ActivatedRoute,
    private router: Router,
    private chartTileDisplayPreferenceService: ChartTileDisplayPreferenceService,
    private modalService: DwModalService,
    private contextService: DwContextService,
    private eventService: DwEventService,
    private securityUserService: DwSecurityUserService,
    adminManager: AdminManagerService
  ) {
    this.expressionContext = { pilexosUser: adminManager.getPilexosUser() };
    this.canEditDashboard = adminManager.canUseAdminProperties();
  }

  ngOnInit(): void {
    this.incomingTile = window.history.state?.reportTileDrillDown;
    this.showChartGridFirstByDefault = this.chartTileDisplayPreferenceService.getReportsShowGridFirst();

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const dashboardKey = params.get('dashboard');
      const resolvedTab = this.resolvedTabs.find(item => item.tab.dashboardKey === dashboardKey);
      if (resolvedTab && resolvedTab !== this.activeTab) {
        this.activateTab(resolvedTab);
      }
    });

    const componentConfig = (this.route.snapshot.data?.componentConfig || {}) as ReportDashboardRouteConfig;
    if (componentConfig.dashboardKey) {
      this.loadStandaloneDashboard(componentConfig.dashboardKey);
      return;
    }

    // Compatibility fallback for routes created before dashboardGroupKey moved
    // under RouteConfigJSON.componentConfig.
    const routePath = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (componentConfig.dashboardGroupKey) {
      this.loadDashboardGroup(componentConfig.dashboardGroupKey);
      return;
    }
    if (routePath.indexOf('/') < 0) {
      this.loadDashboardGroup(routePath);
      return;
    }
    this.handleError('Select a dashboard group or standalone dashboard in this route\'s component options.');
  }

  ngOnDestroy(): void {
    this.contextRefreshSubscription?.unsubscribe();
    this.contextRefreshKey = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeIncomingTile(): void {
    this.incomingTile = null;
    const state = { ...window.history.state };
    delete state.reportTileDrillDown;
    window.history.replaceState(state, '');
  }

  @HostListener('window:beforeunload', ['$event'])
  preventNavigationWithUnsavedChanges(event: BeforeUnloadEvent): void {
    if (!this.designMode || !this.designerDirty) {
      return;
    }
    event.preventDefault();
    event.returnValue = '';
  }

  selectTab(resolvedTab: ResolvedDashboardTab, discardConfirmed = false): void {
    if (!resolvedTab || resolvedTab === this.activeTab) {
      return;
    }

    const reopenDesigner = this.designMode;
    if (reopenDesigner && !discardConfirmed) {
      this.confirmDiscardChanges(() => this.selectTab(resolvedTab, true));
      return;
    }
    this.closeDesigner();
    this.activateTab(resolvedTab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { dashboard: resolvedTab.tab.dashboardKey },
      queryParamsHandling: 'merge'
    });
    if (reopenDesigner) {
      this.editDashboard();
    }
  }

  selectTabByKey(dashboardKey: string): void {
    if (dashboardKey === ADD_NEW_DASHBOARD_KEY) {
      this.openAddNewDashboard();
      return;
    }
    const resolvedTab = this.resolvedTabs.find(item => item.tab.dashboardKey === dashboardKey);
    if (resolvedTab) {
      this.selectTab(resolvedTab);
    }
  }

  onShowChartGridFirstByDefaultChanged(showGridFirst: boolean): void {
    this.showChartGridFirstByDefault = !!showGridFirst;
    this.chartTileDisplayPreferenceService.setReportsShowGridFirst(this.showChartGridFirstByDefault);
  }

  onDashboardDefaultsChanged(definition: ReportDashboardDefinition): void {
    if (this.activeTab) {
      this.activeTab.definition = definition;
    }
  }

  printReport(): void {
    this.dashboardComponent?.printReport();
  }

  editDashboard(): void {
    if (!this.canEditDashboard || !this.activeDashboardKey || this.designerLoading || this.designerSaving) {
      return;
    }

    this.designerPreviewFilters = this.copyDesignerPreviewFilters();
    this.designerLoading = true;
    this.designerErrorMessage = null;
    forkJoin([
      this.configService.loadDashboardRecord(this.activeDashboardKey),
      this.configService.loadReusableWidgetRecords().pipe(catchError(() => of([] as ReportDashboardWidgetConfigRecord[])))
    ]).pipe(takeUntil(this.destroy$)).subscribe(([record, reusableWidgetRecords]) => {
      try {
        this.activeDashboardRecord = this.clone(record);
        this.workingDefinition = this.prepareWorkingDefinition(
          this.parseConfig(record.DashboardConfigJSON)
        );
        this.workingConfigText = JSON.stringify(this.workingDefinition, null, 2);
        this.reusableWidgetRecords = (reusableWidgetRecords || []).filter(item => item.IsActive);
        this.designerEditorMode = 'design';
        this.designerDirty = false;
        this.designMode = true;
      } catch (error) {
        this.designerErrorMessage = error?.message || error || 'The dashboard configuration could not be opened.';
      }
      this.designerLoading = false;
    }, error => {
      this.designerLoading = false;
      this.designerErrorMessage = error?.message || error || 'The dashboard configuration could not be opened.';
    });
  }

  selectDesignerEditorMode(mode: 'design' | 'json'): void {
    if (mode === this.designerEditorMode) {
      return;
    }
    if (mode === 'design' && !this.parseWorkingConfigText()) {
      return;
    }
    if (mode === 'json') {
      this.workingConfigText = JSON.stringify(this.workingDefinition, null, 2);
    }
    this.designerEditorMode = mode;
    this.designerErrorMessage = null;
  }

  onDesignerDefinitionChanged(definition: ReportDashboardDefinition): void {
    this.workingDefinition = definition;
    this.workingConfigText = JSON.stringify(definition, null, 2);
    this.designerDirty = true;
    this.designerErrorMessage = null;
  }

  onWorkingConfigTextChanged(): void {
    this.designerDirty = true;
    this.designerErrorMessage = null;
  }

  saveDashboard(): void {
    if (!this.activeDashboardRecord || this.designerSaving) {
      return;
    }
    if (this.designerEditorMode === 'json' && !this.parseWorkingConfigText()) {
      return;
    }

    const storedDefinition: any = this.clone(this.workingDefinition);
    delete storedDefinition.key;
    delete storedDefinition.contextNames;
    storedDefinition.filters = (storedDefinition.filters || []).map(filter => {
      const storedFilter = { ...(filter || {}) };
      delete storedFilter.visibleExpression;
      return storedFilter;
    });

    const record = this.clone(this.activeDashboardRecord);
    record.DashboardConfigJSON = JSON.stringify(storedDefinition, null, 2);
    this.designerSaving = true;
    this.designerErrorMessage = null;

    this.configService.parseDashboardRecord(record).pipe(
      mergeMap(() => this.configService.saveDashboardRecord(record)),
      mergeMap(savedRecord => this.configService.loadDashboard(record.DashboardKey).pipe(
        map(definition => ({ savedRecord, definition }))
      )),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      this.activeDashboardRecord = this.clone(result.savedRecord);
      this.definition = result.definition;
      if (this.activeTab) {
        this.activeTab.definition = result.definition;
      }
      this.designerSaving = false;
      this.closeDesigner();
    }, error => {
      this.designerSaving = false;
      this.designerErrorMessage = error?.message || error || 'The dashboard configuration could not be saved.';
    });
  }

  cancelDashboardDesign(discardConfirmed = false): void {
    if (!discardConfirmed) {
      this.confirmDiscardChanges(() => this.cancelDashboardDesign(true));
      return;
    }
    this.closeDesigner();
  }

  manageDashboardItems(): void {
    if (!this.groupDefinition?.key || this.designerGroupSaving) {
      return;
    }

    this.designerGroupSaving = true;
    this.designerErrorMessage = null;
    forkJoin([
      this.configService.loadDashboardGroupRecord(this.groupDefinition.key),
      this.configService.loadDashboardRecords(),
      this.configService.loadReusableWidgetRecords().pipe(
        catchError(() => of([] as ReportDashboardWidgetConfigRecord[]))
      )
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([record, dashboardRecords, widgetRecords]) => {
        this.designerGroupSaving = false;
        let definition: ReportDashboardGroupDefinition;
        try {
          definition = this.configService.parseDashboardGroupRecord(record);
        } catch (error) {
          this.designerErrorMessage = error?.message || error || 'Dashboard items could not be loaded.';
          return;
        }

        const modalConfig = new DwModalConfig();
        modalConfig.title = 'Manage Dashboard Group';
        modalConfig.component = ReportDashboardGroupModalComponent;
        modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
        modalConfig.modalSize = DwModalSize.custom;
        modalConfig.customModalWidth = '1400px';
        modalConfig.customModalHeight = '850px';
        modalConfig.allowFullScreen = true;
        modalConfig.cloneData = true;
        modalConfig.data = {
          groupDisplay: record.DashboardGroupDisplay,
          definition,
          dashboardRecords,
          widgetRecords
        };

        this.modalService.showModal(modalConfig).subscribe(result => {
          if (!result.cancelled && result.data) {
            const modalResult = result.data as ReportDashboardGroupModalResult;
            this.saveDashboardGroupItems(record, modalResult.definition, modalResult.dashboardClones);
          }
        });
      }, error => {
        this.designerGroupSaving = false;
        this.designerErrorMessage = error?.message || error || 'Dashboard items could not be loaded.';
      });
  }

  openAddNewDashboard(discardConfirmed = false): void {
    if (!this.groupDefinition?.key || this.designerGroupSaving || !this.canEditDashboard) {
      return;
    }
    if (!discardConfirmed) {
      this.confirmDiscardChanges(() => this.openAddNewDashboard(true));
      return;
    }

    this.designerGroupSaving = true;
    this.designerErrorMessage = null;
    forkJoin([
      this.configService.loadDashboardGroupRecord(this.groupDefinition.key),
      this.configService.loadDashboardRecords()
    ]).pipe(takeUntil(this.destroy$)).subscribe(([groupRecord, dashboardRecords]) => {
      this.designerGroupSaving = false;

      let groupDefinition: ReportDashboardGroupDefinition;
      try {
        groupDefinition = this.configService.parseDashboardGroupRecord(groupRecord);
      } catch (error) {
        this.designerErrorMessage = error?.message || error || 'The dashboard group could not be loaded.';
        return;
      }

      const modalConfig = new DwModalConfig();
      modalConfig.title = 'Add New Dashboard';
      modalConfig.component = ReportDashboardCreateModalComponent;
      modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
      modalConfig.modalSize = DwModalSize.large;
      modalConfig.cloneData = true;
      modalConfig.data = {
        groupDisplay: groupRecord.DashboardGroupDisplay,
        existingDashboardKeys: dashboardRecords.map(record => record.DashboardKey),
        defaultDataSourceItemName: this.workingDefinition?.dataSourceItemName
          || this.definition?.dataSourceItemName
          || 'roCheckrideFactBase',
        simpleMode: true
      };

      this.modalService.showModal(modalConfig).subscribe(result => {
        if (!result.cancelled && result.data) {
          this.createDashboardInGroup(
            groupRecord,
            groupDefinition,
            result.data as ReportDashboardCreateModalResult
          );
        }
      });
    }, error => {
      this.designerGroupSaving = false;
      this.designerErrorMessage = error?.message || error || 'The dashboard group could not be loaded.';
    });
  }

  get displayedDashboardNavigationItems(): DashboardNavigationItem[] {
    if (!this.designMode || !this.groupDefinition || !this.canEditDashboard) {
      return this.dashboardNavigationItems;
    }
    return [
      ...this.dashboardNavigationItems,
      { dashboardKey: ADD_NEW_DASHBOARD_KEY, display: 'Add New' }
    ];
  }

  get showGroupNavigation(): boolean {
    if (!this.groupDefinition || !this.resolvedTabs.length) {
      return false;
    }
    return this.designMode
      || !(this.groupDefinition.hideTabsWhenSingleVisible && this.resolvedTabs.length === 1);
  }

  get showDashboardHeader(): boolean {
    return !this.showGroupNavigation;
  }

  get groupTitle(): string {
    return this.groupDefinition?.key === 'school-reports'
      ? 'School Report'
      : this.groupDefinition?.title;
  }

  get designerTitle(): string {
    return this.workingDefinition?.title || this.definition?.title || 'Dashboard';
  }

  private loadStandaloneDashboard(dashboardKey: string): void {
    this.loading = true;
    this.configService.loadDashboard(dashboardKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe(definition => {
        this.activeDashboardKey = dashboardKey;
        this.definition = definition;
        this.loading = false;
      }, error => this.handleError(error));
  }

  private loadDashboardGroup(groupKey: string): void {
    if (!groupKey) {
      this.handleError('A dashboardGroupKey or dashboardKey route option is required.');
      return;
    }

    const generation = ++this.groupLoadGeneration;
    this.loading = true;
    this.configService.loadDashboardGroup(groupKey).pipe(
      mergeMap(group => {
        return this.resolveGroupContexts(group).pipe(map(expressionContext => ({ group, expressionContext })));
      }),
      mergeMap(({ group, expressionContext }) => {
        const visibleTabs = group.tabs.filter(tab => this.isVisible(tab.visibleExpression, expressionContext));
        if (!visibleTabs.length) {
          return of({ group, expressionContext, resolvedTabs: [] as ResolvedDashboardTab[] });
        }

        return forkJoin(visibleTabs.map(tab => this.configService.loadDashboard(tab.dashboardKey).pipe(
          map(definition => ({ tab, definition } as ResolvedDashboardTab)),
          catchError(() => of(null as ResolvedDashboardTab))
        ))).pipe(map(tabs => ({
          group,
          expressionContext,
          resolvedTabs: tabs.filter(tab => !!tab)
        })));
      }),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      if (generation !== this.groupLoadGeneration) {
        return;
      }
      this.groupDefinition = result.group;
      this.expressionContext = result.expressionContext;
      this.resolvedTabs = result.resolvedTabs;
      this.dashboardNavigationItems = result.resolvedTabs.map(item => ({
        dashboardKey: item.tab.dashboardKey,
        display: item.tab.display
      }));
      if (!result.resolvedTabs.length) {
        this.handleError('No dashboards are currently available for this page.');
        return;
      }

      const requestedKey = this.route.snapshot.queryParamMap.get('dashboard');
      const initial = result.resolvedTabs.find(item => item.tab.dashboardKey === requestedKey)
        || result.resolvedTabs.find(item => item.tab.dashboardKey === this.groupDefinition.defaultDashboardKey)
        || result.resolvedTabs[0];
      this.activateTab(initial);
      this.watchGroupContexts(this.groupDefinition);
      this.loading = false;
    }, error => {
      if (generation === this.groupLoadGeneration) {
        this.handleError(error);
      }
    });
  }

  private activateTab(resolvedTab: ResolvedDashboardTab): void {
    this.activeTab = resolvedTab;
    this.activeDashboardKey = resolvedTab.tab.dashboardKey;
    this.definition = resolvedTab.definition;
    this.errorMessage = null;
  }

  private resolveGroupContexts(group: ReportDashboardGroupDefinition): Observable<any> {
    return this.contextService.waitForContextDefLoad().pipe(
      take(1),
      mergeMap(() => this.contextService.getContextsAndResolve(group?.contextNames || [], {})),
      take(1)
    );
  }

  private watchGroupContexts(group: ReportDashboardGroupDefinition): void {
    const contextNames = group?.contextNames || [];
    const refreshKey = `${group?.key || ''}|${contextNames
      .map(contextName => contextName.toLowerCase())
      .sort()
      .join('|')}`;
    if (this.contextRefreshSubscription && this.contextRefreshKey === refreshKey) {
      return;
    }

    this.contextRefreshSubscription?.unsubscribe();
    this.contextRefreshKey = refreshKey;
    const topics = this.contextService.getContextTopics(contextNames);

    // Mirror CAP's configurable-context lifecycle with directly owned event
    // subscriptions so switching groups or destroying the page tears them down.
    // CAP topics use BehaviorSubjects, so exclude the event already present when
    // this watcher is created without skipping the first future Subject event.
    const refreshSources = [
      this.securityUserService.securityContextChanged().pipe(skip(1)),
      ...topics.map(topic => {
        const existingEvent = this.eventService.getTopicLastEvent(topic);
        return this.eventService.getEvent(topic).pipe(filter(event => event !== existingEvent));
      })
    ];
    this.contextRefreshSubscription = merge(...refreshSources).pipe(
      debounceTime(25),
      takeUntil(this.destroy$)
    ).subscribe(() => this.loadDashboardGroup(group.key));
  }

  private parseWorkingConfigText(): boolean {
    try {
      this.workingDefinition = this.prepareWorkingDefinition(this.parseConfig(this.workingConfigText));
      this.workingConfigText = JSON.stringify(this.workingDefinition, null, 2);
      this.designerErrorMessage = null;
      return true;
    } catch (error) {
      this.designerErrorMessage = `Configuration JSON is invalid: ${error?.message || error}`;
      return false;
    }
  }

  private parseConfig(config: any): ReportDashboardDefinition {
    if (config && typeof config === 'object') {
      return this.clone(config);
    }
    if (!config || typeof config !== 'string') {
      throw new Error('Configuration JSON is missing.');
    }
    return JSON.parse(config) as ReportDashboardDefinition;
  }

  private prepareWorkingDefinition(definition: ReportDashboardDefinition): ReportDashboardDefinition {
    const working: any = this.clone(definition || {});
    delete working.contextNames;
    working.filters = (working.filters || []).map(filter => {
      const normalized = { ...(filter || {}) };
      delete normalized.visibleExpression;
      return normalized;
    });
    working.summaries = working.summaries || [];
    working.tiles = working.tiles || [];
    working.widgets = working.widgets || [];
    working.drillDowns = working.drillDowns || [];
    return working as ReportDashboardDefinition;
  }

  private confirmDiscardChanges(onConfirmed: () => void, message = 'Discard your unsaved dashboard changes?'): void {
    if (!this.designerDirty) {
      onConfirmed();
      return;
    }

    this.modalService.showDialog(
      'Discard Unsaved Changes?',
      message,
      DwModalButtonStandardConfigs.instance.YesOrNo
    ).pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      if (result.buttonCommand === DwButtonCommand.yes) {
        onConfirmed();
      }
    });
  }

  private closeDesigner(): void {
    this.designMode = false;
    this.designerLoading = false;
    this.designerSaving = false;
    this.designerGroupSaving = false;
    this.designerDirty = false;
    this.designerErrorMessage = null;
    this.designerEditorMode = 'design';
    this.workingDefinition = null;
    this.workingConfigText = '';
    this.activeDashboardRecord = null;
  }

  private clone<T>(value: T): T {
    return value === undefined || value === null
      ? value
      : JSON.parse(JSON.stringify(value));
  }

  private copyDesignerPreviewFilters(): DwQueryFilterGroup {
    const copied = new DwQueryFilterGroup();
    const currentFilters = this.dashboardComponent?.currentFilters;
    if (currentFilters?.Filters?.length) {
      copied.Filters = this.clone(currentFilters.Filters);
    }
    return copied;
  }

  private saveDashboardGroupItems(
    sourceRecord: ReportDashboardGroupConfigRecord,
    definition: ReportDashboardGroupDefinition,
    dashboardClones: ReportDashboardPendingClone[] = [],
    discardConfirmed = false
  ): void {
    const removesActiveDashboard = !definition.tabs.some(tab => tab.dashboardKey === this.activeDashboardKey);
    if (removesActiveDashboard && this.designerDirty && !discardConfirmed) {
      this.confirmDiscardChanges(
        () => this.saveDashboardGroupItems(sourceRecord, definition, dashboardClones, true),
        'This removes the dashboard currently being edited. Discard its unsaved changes?'
      );
      return;
    }

    const storedDefinition: any = this.clone(definition);
    delete storedDefinition.key;
    const record = this.clone(sourceRecord);
    record.DashboardGroupConfigJSON = JSON.stringify(storedDefinition, null, 2);
    try {
      this.configService.parseDashboardGroupRecord(record);
    } catch (error) {
      this.designerErrorMessage = error?.message || error || 'Dashboard item configuration is invalid.';
      return;
    }

    this.designerGroupSaving = true;
    const createClones$ = dashboardClones.length
      ? forkJoin(dashboardClones.map(clone => this.configService.createDashboardRecord(clone.record)))
      : of([] as ReportDashboardConfigRecord[]);
    createClones$.pipe(
        mergeMap(() => this.configService.saveDashboardGroupRecord(record)),
        mergeMap(savedRecord => {
          const updatedGroup = this.configService.parseDashboardGroupRecord(savedRecord);
          return this.resolveGroupContexts(updatedGroup).pipe(mergeMap(expressionContext => {
            this.expressionContext = expressionContext;
            const existingByKey = this.resolvedTabs.reduce((result, resolvedTab) => {
              result[resolvedTab.tab.dashboardKey] = resolvedTab;
              return result;
            }, {} as { [key: string]: ResolvedDashboardTab });
            const visibleTabs = updatedGroup.tabs.filter(tab => this.isVisible(tab.visibleExpression));
            const resolvedTabs$ = visibleTabs.length
              ? forkJoin(visibleTabs.map(tab => existingByKey[tab.dashboardKey]
                ? of({ tab, definition: existingByKey[tab.dashboardKey].definition } as ResolvedDashboardTab)
                : this.configService.loadDashboard(tab.dashboardKey).pipe(
                  map(dashboardDefinition => ({ tab, definition: dashboardDefinition } as ResolvedDashboardTab)),
                  catchError(() => of(null as ResolvedDashboardTab))
                )))
              : of([] as ResolvedDashboardTab[]);
            return resolvedTabs$.pipe(map(resolvedTabs => ({
              updatedGroup,
              expressionContext,
              resolvedTabs: resolvedTabs.filter(tab => !!tab)
            })));
          }));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.designerGroupSaving = false;
        this.groupDefinition = result.updatedGroup;
        this.expressionContext = result.expressionContext;
        this.resolvedTabs = result.resolvedTabs;
        this.dashboardNavigationItems = this.resolvedTabs.map(item => ({
          dashboardKey: item.tab.dashboardKey,
          display: item.tab.display
        }));

        const nextActive = this.resolvedTabs.find(item => item.tab.dashboardKey === this.activeDashboardKey)
          || this.resolvedTabs.find(item => item.tab.dashboardKey === result.updatedGroup.defaultDashboardKey)
          || this.resolvedTabs[0];
        if (!nextActive) {
          this.handleError('No dashboards are currently available for this page.');
          return;
        }
        if (removesActiveDashboard) {
          this.closeDesigner();
          this.activateTab(nextActive);
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { dashboard: nextActive.tab.dashboardKey },
            queryParamsHandling: 'merge'
          });
        } else {
          this.activeTab = nextActive;
          this.definition = { ...nextActive.definition };
        }
        this.watchGroupContexts(result.updatedGroup);
      }, error => {
        this.designerGroupSaving = false;
        this.designerErrorMessage = error?.message || error || 'Dashboard items could not be saved.';
      });
  }

  private createDashboardInGroup(
    sourceGroupRecord: ReportDashboardGroupConfigRecord,
    sourceGroupDefinition: ReportDashboardGroupDefinition,
    values: ReportDashboardCreateModalResult
  ): void {
    const groupDefinition = this.clone(sourceGroupDefinition);
    groupDefinition.tabs.push({
      dashboardKey: values.dashboardKey,
      display: values.tabDisplay,
      iconClass: values.iconClass || undefined
    });
    if (!groupDefinition.defaultDashboardKey) {
      groupDefinition.defaultDashboardKey = values.dashboardKey;
    }

    const storedGroupDefinition: any = this.clone(groupDefinition);
    delete storedGroupDefinition.key;
    const groupRecord = this.clone(sourceGroupRecord);
    groupRecord.DashboardGroupConfigJSON = JSON.stringify(storedGroupDefinition, null, 2);

    try {
      this.configService.parseDashboardGroupRecord(groupRecord);
    } catch (error) {
      this.designerErrorMessage = error?.message || error || 'The dashboard group configuration is invalid.';
      return;
    }

    const dashboardConfig = {
      schemaVersion: 1,
      title: values.title,
      description: values.description || undefined,
      iconClass: values.iconClass || undefined,
      dataSourceItemName: values.dataSourceItemName || undefined,
      allowPrint: true,
      layoutColumnCount: 12,
      filters: [],
      summaries: [],
      tiles: [],
      widgets: [],
      drillDowns: []
    };

    this.designerGroupSaving = true;
    this.designerErrorMessage = null;
    this.configService.createDashboardRecord({
      DashboardKey: values.dashboardKey,
      DashboardDisplay: values.dashboardDisplay,
      DashboardConfigJSON: JSON.stringify(dashboardConfig, null, 2),
      IsActive: values.isActive
    }).pipe(
      mergeMap(() => this.configService.saveDashboardGroupRecord(groupRecord)),
      mergeMap(savedGroupRecord => this.configService.loadDashboard(values.dashboardKey).pipe(
        map(dashboardDefinition => ({ savedGroupRecord, dashboardDefinition }))
      )),
      takeUntil(this.destroy$)
    ).subscribe(({ savedGroupRecord, dashboardDefinition }) => {
      const updatedGroup = this.configService.parseDashboardGroupRecord(savedGroupRecord);
      const newTab = updatedGroup.tabs.find(tab => tab.dashboardKey === values.dashboardKey);
      const newResolvedTab = { tab: newTab, definition: dashboardDefinition } as ResolvedDashboardTab;
      const existingByKey = this.resolvedTabs.reduce((result, resolvedTab) => {
        result[resolvedTab.tab.dashboardKey] = resolvedTab;
        return result;
      }, {} as { [key: string]: ResolvedDashboardTab });
      existingByKey[values.dashboardKey] = newResolvedTab;

      this.groupDefinition = updatedGroup;
      this.resolvedTabs = updatedGroup.tabs
        .filter(tab => this.isVisible(tab.visibleExpression))
        .map(tab => existingByKey[tab.dashboardKey])
        .filter(tab => !!tab);
      this.dashboardNavigationItems = this.resolvedTabs.map(item => ({
        dashboardKey: item.tab.dashboardKey,
        display: item.tab.display
      }));

      this.closeDesigner();
      this.activateTab(newResolvedTab);
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { dashboard: values.dashboardKey },
        queryParamsHandling: 'merge'
      });
      this.editDashboard();
    }, error => {
      this.designerGroupSaving = false;
      this.designerErrorMessage = error?.message || error || 'The dashboard could not be created.';
    });
  }

  private isVisible(expression: any, context = this.expressionContext): boolean {
    if (!expression) {
      return true;
    }
    return this.expressionState.evaluateExpressionWithResolvedData(expression, {}, context || {});
  }

  private handleError(error: any): void {
    this.loading = false;
    this.errorMessage = error?.message || error || 'The report dashboard could not be loaded.';
  }
}
